import {GoingNewUtils} from 'xmgoing-utils';
import {isDynamicDefaultToken} from '@/main-page-base/ctls/containers/xmQueryForm/utils/queryFormUtils';
import {CtlComEnum} from '@/main-page/enums/ctlComEnum';
import {resolveShortcutDefaultValue} from '@/main-page/utils/initValues';

export function isXmSubTableColumn(colItem: any) {
  return colItem?.controlType === 'xmSubTable' || colItem?.propType === 'xmSubTable';
}

/** 动态子表格列是否开启嵌套展示（主表展开行内联子表） */
export function isNestedDisplaySubTableColumn(colItem: any) {
  if (!isXmSubTableColumn(colItem)) {
    return false;
  }
  return GoingNewUtils.parseBoolean(colItem.nestedDisplay) === true;
}

/** 子表数据在主行 record 上的字段名 */
export function getSubTableFieldKey(subCol: any) {
  return subCol?.dataIndex || subCol?.propId;
}

/** 读取主行上某动态子表列对应的子表数据（只读，不修改 record） */
export function getRecordSubTableRecords(record: any, subCol: any): any[] {
  if (!record || !subCol) {
    return [];
  }
  const fieldKey = getSubTableFieldKey(subCol);
  const data = record[fieldKey];
  if (data == null || data === '') {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** 主行是否至少有一个嵌套子表列存在数据 */
export function hasRecordNestedSubTableData(record: any, subTableColumns: any[]): boolean {
  const cols = subTableColumns || [];
  if (!record || !cols.length) {
    return false;
  }
  return cols.some((subCol) => getRecordSubTableRecords(record, subCol).length > 0);
}

const SKIP_DEFAULT_CONTROL_TYPES = new Set(['operation', 'orderNum', 'columnsConfig']);

/**
 * 数据层默认值处理模式：
 * - addRow：增行时补值（自定义 + 标量 + 快捷配置）
 * - validate：校验前仅补快捷配置 + 自定义（不补无 defaultSet 的标量默认）
 * - load：加载/追加后补值（标量 + 快捷配置，保证未渲染分页行内存数据完整）
 */
export type ApplySubTableDefaultMode = 'addRow' | 'validate' | 'load';

function isLiteralScalarDefault(defaultValue: any) {
  if (defaultValue == null || defaultValue === '') {
    return false;
  }
  if (Array.isArray(defaultValue)) {
    return false;
  }
  const valueType = typeof defaultValue;
  return valueType === 'string' || valueType === 'number' || valueType === 'boolean';
}

/** 快捷配置列（currentDateTime / currentUserId 等） */
function isShortcutConfigColumn(col: any) {
  const defaultSet = col?.defaultSet;
  return defaultSet === CtlComEnum.COMMON_DEF_VAL || defaultSet === '快捷配置';
}

/** 必须由组件/公式引擎解析，数据层不处理 */
function isNonDataLayerDefault(col: any) {
  if (col?.defaultSetVuex) {
    return true;
  }
  const defaultSet = col?.defaultSet;
  return defaultSet === CtlComEnum.GONG_SI_EDITOR
    || defaultSet === '公式编辑'
    || defaultSet === 'session缓存'
    || defaultSet === '全局状态变量';
}

function isShortcutPlaceholderValue(col: any, value: any) {
  if (!isShortcutConfigColumn(col)) {
    return false;
  }
  const shortcutKey = col?.defaultValue;
  return shortcutKey != null && shortcutKey !== '' && value === shortcutKey;
}

function hasFieldValue(value: any) {
  return value != null && value !== '';
}

function resolveShortcutColumnDefault(col: any) {
  const shortcutKey = col?.defaultValue;
  if (shortcutKey == null || shortcutKey === '') {
    return undefined;
  }
  return resolveShortcutDefaultValue(shortcutKey, {
    format: col?.format,
    defFormat: col?.format || 'YYYY-MM-DD',
  });
}

/**
 * 解析数据层可写入的列默认值
 */
function resolveColumnDefaultValue(col: any, mode: ApplySubTableDefaultMode) {
  if (isNonDataLayerDefault(col)) {
    return undefined;
  }

  // 快捷配置：同步解析后写入（解决前端分页未渲染行）
  if (isShortcutConfigColumn(col)) {
    if (mode === 'addRow' || mode === 'validate' || mode === 'load') {
      return resolveShortcutColumnDefault(col);
    }
    return undefined;
  }

  // 改前 addRow 已支持「自定义」
  if (col?.defaultSet === CtlComEnum.USER_DEF_VAL || col?.defaultSet === '自定义') {
    const val = col?.defaultValue;
    if (val != null && val !== '') {
      return val;
    }
    return undefined;
  }

  // 无 defaultSet 的标量 defaultValue（如 dataType=gongxu）：load/addRow 写入，validate 不补齐
  if (col?.defaultSet) {
    return undefined;
  }
  if (!isLiteralScalarDefault(col?.defaultValue)) {
    return undefined;
  }
  if (isDynamicDefaultToken(col.defaultValue)) {
    return undefined;
  }
  if (mode === 'addRow' || mode === 'load') {
    return col.defaultValue;
  }

  return undefined;
}

/**
 * 为子表行处理列默认值
 */
export function applySubTableColumnDefaults(
  record: any,
  columns: any[] = [],
  mode: ApplySubTableDefaultMode = 'validate',
) {
  if (!record || !columns?.length) {
    return;
  }
  columns.forEach((col) => {
    const propId = col?.propId || col?.dataIndex;
    if (!propId || SKIP_DEFAULT_CONTROL_TYPES.has(col?.controlType) || col?.propType === 'operation') {
      return;
    }

    const currentVal = record[propId];

    if (isShortcutPlaceholderValue(col, currentVal)) {
      delete record[propId];
    } else if (hasFieldValue(currentVal)) {
      return;
    }

    const defaultValue = resolveColumnDefaultValue(col, mode);
    if (defaultValue != null && defaultValue !== '') {
      record[propId] = defaultValue;
    }
  });
}

/** 批量为子表行处理列默认值 */
export function applySubTableRecordsDefaults(
  records: any[],
  columns: any[] = [],
  mode: ApplySubTableDefaultMode = 'validate',
) {
  if (!records?.length || !columns?.length) {
    return;
  }
  records.forEach((record) => applySubTableColumnDefaults(record, columns, mode));
}
